import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Checkbox } from '../../../components/Checkbox';
import type { AdminTourFieldError, AdminTourStep } from '../../../types/adminTours';
import { TourBadge } from './TourBadge';
import { canDeleteStep, isUnpublishedStep } from './tourDraft';
import { anchorSummary } from './tourPresentation';
import { TourStepForm } from './TourStepForm';

const DragHandleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="6" cy="4" r="1.15" />
    <circle cx="10" cy="4" r="1.15" />
    <circle cx="6" cy="8" r="1.15" />
    <circle cx="10" cy="8" r="1.15" />
    <circle cx="6" cy="12" r="1.15" />
    <circle cx="10" cy="12" r="1.15" />
  </svg>
);

interface StepRowProps {
  step: AdminTourStep;
  index: number;
  count: number;
  errors: AdminTourFieldError[];
  isEditing: boolean;
  onToggleEnabled: (key: string, enabled: boolean) => void;
  onMove: (key: string, delta: -1 | 1) => void;
  registerMoveButton: (key: string, direction: 'up' | 'down', node: HTMLButtonElement | null) => void;
  onEdit: (key: string | null) => void;
  onChangeCopy: (key: string, title: string, body: string) => void;
  onRevert: (key: string) => void;
  onDelete: (key: string) => void;
}

const StepRow = ({
  step,
  index,
  count,
  errors,
  isEditing,
  onToggleEnabled,
  onMove,
  registerMoveButton,
  onEdit,
  onChangeCopy,
  onRevert,
  onDelete,
}: StepRowProps) => {
  const readOnly = step.removedFromCode;
  // An unpublished step is always on: the draft change set carries no enabled flag for a step the
  // API has not keyed yet, so a toggle here would look like it saved and would not.
  const unpublished = isUnpublishedStep(step);
  // A step added a moment ago has no copy yet, and every control on this row names itself after the
  // title. Without a stand-in, the drag handle, the toggle and the move buttons announce as "Show",
  // "Reorder" and nothing else.
  const name = step.title || 'this new step';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.key,
    disabled: readOnly,
  });
  const formId = `tour-step-form-${step.key}`;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'tour-steps__row',
        isDragging ? 'tour-steps__row--dragging' : '',
        readOnly ? 'tour-steps__row--read-only' : '',
        step.isEnabled ? '' : 'tour-steps__row--off',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`tour-step-row-${step.key}`}
    >
      <div className="tour-steps__row-head">
        {/* The handle owns the drag, not the row, so the checkbox stays clickable. */}
        <button
          type="button"
          className="tour-steps__handle"
          aria-label={`Reorder ${name}`}
          disabled={readOnly}
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon />
        </button>

        <Checkbox
          className="tour-steps__toggle"
          checked={step.isEnabled}
          disabled={readOnly || unpublished}
          aria-label={`Show ${name}`}
          onChange={(event) => onToggleEnabled(step.key, event.target.checked)}
          data-testid={`tour-step-enabled-${step.key}`}
        />

        <div className="tour-steps__text">
          <span className={`tour-steps__title${step.title ? '' : ' tour-steps__title--empty'}`}>
            {step.title || 'Untitled step'}
          </span>
          <span className="tour-steps__anchor">{anchorSummary(step)}</span>
          {/* No role: the failure banner announces these, and one alert per affected row on top
              of it would read the same failure N+1 times. */}
          {errors.length > 0 && (
            <span className="tour-steps__error">
              {errors.map((error) => error.message).join(' ')}
            </span>
          )}
        </div>

        <div className="tour-steps__badges">
          {unpublished && <TourBadge tone="draft">Unpublished</TourBadge>}
          {step.isNewFromCode && <TourBadge tone="new">New from code</TourBadge>}
          {step.removedFromCode && <TourBadge tone="removed">Removed from code</TourBadge>}
          {step.source === 'Admin' && <TourBadge tone="admin">Admin</TourBadge>}
        </div>

        {/*
          Drag is a mouse gesture first. These two buttons are the keyboard path, and they are on
          every row rather than only the ones that can move - a button that appears and disappears
          as a row reaches an end is harder to use than one that is simply disabled.
        */}
        <div className="tour-steps__move">
          <button
            type="button"
            className="tour-steps__move-button"
            ref={(node) => registerMoveButton(step.key, 'up', node)}
            disabled={readOnly || index === 0}
            aria-label={`Move ${name} up`}
            onClick={() => onMove(step.key, -1)}
            data-testid={`tour-step-up-${step.key}`}
          >
            Move up
          </button>
          <button
            type="button"
            className="tour-steps__move-button"
            ref={(node) => registerMoveButton(step.key, 'down', node)}
            disabled={readOnly || index === count - 1}
            aria-label={`Move ${name} down`}
            onClick={() => onMove(step.key, 1)}
            data-testid={`tour-step-down-${step.key}`}
          >
            Move down
          </button>
        </div>

        {readOnly ? (
          <span className="tour-steps__read-only-note">
            Read only: the app no longer has this step.
          </span>
        ) : (
          <>
            <button
              type="button"
              className="tour-steps__edit"
              aria-expanded={isEditing}
              aria-controls={formId}
              onClick={() => onEdit(isEditing ? null : step.key)}
              data-testid={`tour-step-edit-${step.key}`}
            >
              {isEditing ? 'Close' : 'Edit copy'}
            </button>
            {/* Only an admin's own step. A code step belongs to the manifest, and deleting it here
                would be undone by the next sync. */}
            {canDeleteStep(step) && (
              <button
                type="button"
                className="tour-steps__delete"
                aria-label={`Delete ${name}`}
                onClick={() => onDelete(step.key)}
                data-testid={`tour-step-delete-${step.key}`}
              >
                Delete
              </button>
            )}
          </>
        )}
      </div>

      {isEditing && !readOnly && (
        <div id={formId}>
          <TourStepForm
            step={step}
            errors={errors}
            onChange={(title, body) => onChangeCopy(step.key, title, body)}
            onRevert={() => onRevert(step.key)}
            onClose={() => onEdit(null)}
          />
        </div>
      )}
    </li>
  );
};

interface TourStepListProps {
  steps: AdminTourStep[];
  errorsByStep: Record<string, AdminTourFieldError[]>;
  editingKey: string | null;
  labelledBy: string;
  onReorder: (key: string, overKey: string) => void;
  onToggleEnabled: (key: string, enabled: boolean) => void;
  onMove: (key: string, delta: -1 | 1) => void;
  onEdit: (key: string | null) => void;
  onChangeCopy: (key: string, title: string, body: string) => void;
  onRevert: (key: string) => void;
  onDelete: (key: string) => void;
}

/** One platform's steps, in the order they will run. */
export const TourStepList = ({
  steps,
  errorsByStep,
  editingKey,
  labelledBy,
  onReorder,
  onToggleEnabled,
  onMove,
  onEdit,
  onChangeCopy,
  onRevert,
  onDelete,
}: TourStepListProps) => {
  const moveRefs = useRef<
    Record<string, { up: HTMLButtonElement | null; down: HTMLButtonElement | null }>
  >({});
  const [pendingMove, setPendingMove] = useState<{ key: string; direction: 'up' | 'down' } | null>(
    null
  );
  const [announcement, setAnnouncement] = useState('');

  const registerMoveButton = useCallback(
    (key: string, direction: 'up' | 'down', node: HTMLButtonElement | null) => {
      const entry = moveRefs.current[key] ?? { up: null, down: null };
      entry[direction] = node;
      moveRefs.current[key] = entry;
    },
    []
  );

  const handleMove = useCallback(
    (key: string, delta: -1 | 1) => {
      onMove(key, delta);
      setPendingMove({ key, direction: delta === -1 ? 'up' : 'down' });
    },
    [onMove]
  );

  /*
    Two things the drag path gets for free and the button path does not: a screen reader
    announcement, and somewhere for focus to go. A step moved to either end disables the button
    that was just pressed, and a disabled element drops focus to <body> - so focus moves to the
    other button of the same row rather than back to the top of the page.
  */
  useLayoutEffect(() => {
    if (!pendingMove) return;
    setPendingMove(null);
    const index = steps.findIndex((step) => step.key === pendingMove.key);
    if (index < 0) return;
    // Same stand-in as the row's own labels: a step added a moment ago has no title, and this
    // would otherwise announce a leading blank.
    const moved = steps[index].title || 'This new step';
    setAnnouncement(`${moved} moved to position ${index + 1} of ${steps.length}`);
    const entry = moveRefs.current[pendingMove.key];
    const pressed = pendingMove.direction === 'up' ? entry?.up : entry?.down;
    const sibling = pendingMove.direction === 'up' ? entry?.down : entry?.up;
    (pressed && !pressed.disabled ? pressed : sibling)?.focus();
  }, [pendingMove, steps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  if (steps.length === 0) {
    return (
      <p className="tour-steps__empty" data-testid="tour-steps-empty">
        This tour has no steps on this platform yet.
      </p>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={steps.map((step) => step.key)} strategy={verticalListSortingStrategy}>
        {/*
          Named by a paragraph rather than wrapped in a <label>: a label naming a set of controls is
          not a label, and trips label-has-associated-control (see web/CLAUDE.md). No role, either -
          role="group" would override the list role and cost the rows their "5 items" announcement.
        */}
        <ul className="tour-steps" aria-labelledby={labelledBy}>
          {steps.map((step, index) => (
            <StepRow
              key={step.key}
              step={step}
              index={index}
              count={steps.length}
              errors={errorsByStep[step.key] ?? []}
              isEditing={editingKey === step.key}
              onToggleEnabled={onToggleEnabled}
              onMove={handleMove}
              registerMoveButton={registerMoveButton}
              onEdit={onEdit}
              onChangeCopy={onChangeCopy}
              onRevert={onRevert}
              onDelete={onDelete}
            />
          ))}
        </ul>
      </SortableContext>
      {/* Mounted with the list, not with the message: a region inserted along with its text is
          missed by most screen readers. */}
      <p className="tour-steps__live" role="status" data-testid="tour-steps-live">
        {announcement}
      </p>
    </DndContext>
  );
};
